import {IconButton} from '@wandb/components';
import React, {useCallback, useMemo} from 'react';

import {useCriticalWorkflowTrigger} from '../../../../util/datadog/criticalWorkflows/useCriticalWorkflowTrigger';
import {getPanelsPerPage} from '../resizeUtils';
import {useWorkspaceSectionLayoutConfigs} from '../WorkspaceSectionLayoutConfigs';
import {PanelBankSectionPaginationMenu} from './PanelBankSectionPaginationMenu';

export const PanelBankSectionPagination = ({
  activePanelCount,
}: {
  activePanelCount: number;
}) => {
  const {captureInitiatingEvent, observe: observePagination} =
    useCriticalWorkflowTrigger('workspace-section-pagination', {
      windowDurationMs: 1_000,
    });

  const {
    columnsPerPage,
    currentPage,
    flowConfig,
    initialRowsPerPage: rowsPerPage,
    maxNumSectionPages,
    setCurrentPage,
    updateFlowConfig,
  } = useWorkspaceSectionLayoutConfigs();

  const panelsPerPage = useMemo(
    () => getPanelsPerPage({columnsPerPage, rowsPerPage}),
    [columnsPerPage, rowsPerPage]
  );

  const startPanelIndex = useMemo(
    () => currentPage * panelsPerPage,
    [currentPage, panelsPerPage]
  );

  const setNextPage = useCallback(() => {
    observePagination({
      action: 'next_page',
      fromPage: currentPage + 1,
      toPage: currentPage + 2,
      pageSize: panelsPerPage,
      totalItemCount: activePanelCount,
    });
    setCurrentPage(currentPage + 1);
  }, [
    activePanelCount,
    currentPage,
    observePagination,
    panelsPerPage,
    setCurrentPage,
  ]);

  const setPreviousPage = useCallback(() => {
    observePagination({
      action: 'previous_page',
      fromPage: currentPage + 1,
      toPage: currentPage,
      pageSize: panelsPerPage,
      totalItemCount: activePanelCount,
    });
    setCurrentPage(currentPage - 1);
  }, [
    activePanelCount,
    currentPage,
    observePagination,
    panelsPerPage,
    setCurrentPage,
  ]);

  if (activePanelCount <= columnsPerPage) {
    return null;
  }

  return (
    <div
      className="flex items-center"
      onKeyDownCapture={captureInitiatingEvent}
      onPointerDownCapture={captureInitiatingEvent}
      data-test="panel-bank-section-pagination">
      <PanelBankSectionPaginationMenu
        flowConfig={flowConfig}
        panelsPerPage={panelsPerPage}
        startPanelIndex={startPanelIndex}
        updateFlowConfig={updateFlowConfig}
        numTotalPanelsInSection={activePanelCount}
      />
      <IconButton
        isDisabled={currentPage === 0}
        aria-label="Previous page"
        data-dd-action-name="Panel pagination in section"
        data-perf-action="workspace-section-pagination"
        icon="chevron-back"
        onPress={setPreviousPage}
        size="md"
        variant="ghost"
      />
      <IconButton
        isDisabled={currentPage === maxNumSectionPages}
        aria-label="Next page"
        data-dd-action-name="Panel pagination in section"
        data-perf-action="workspace-section-pagination"
        icon="chevron-next"
        onPress={setNextPage}
        size="md"
        variant="ghost"
      />
    </div>
  );
};
